import type { PilotLead, PilotRecord } from "./pilot";

/** Reading order only: these steps do not represent execution or approval. */
export const REVIEW_STEPS = [
  { position: 1, day: "J0", label: "Premier email" },
  { position: 2, day: "J+4", label: "Relance 1" },
  { position: 3, day: "J+11", label: "Relance 2" },
  { position: 4, day: "J+21", label: "Dernier email" },
] as const;

export type ReviewSelection = { code: string; position: number; variant?: "A" | "B" };
export type ReviewCopy = { subject: string; body: string };
export type ReviewDraft = ReviewSelection & ReviewCopy & {
  revision: number;
  original: ReviewCopy;
};

export function reviewSelection(people: PilotLead[], params: URLSearchParams, record?: PilotRecord): ReviewSelection {
  const code = params.get("code");
  const position = Number(params.get("step"));
  const person = people.find((person) => person.code === code) ?? people[0];
  const step = reviewSteps(record).some((item) => item.position === position) ? position : 1;
  const requested = params.get("variant");
  return { code: person?.code ?? "", position: step,
    ...(step === 1 && person?.variant ? {variant: (requested === "A" || requested === "B") && (!record?.profile || record.ab_test.arms.some((a) => a.key === requested)) ? requested : person.variant} : {}) };

}

export function reviewParams(params: URLSearchParams, selection: ReviewSelection): URLSearchParams {
  const next = new URLSearchParams(params);
  next.delete("view"); // Old technical subtab links now open the reader directly.
  next.set("code", selection.code);
  next.set("step", String(selection.position));
  if (selection.position === 1 && selection.variant) next.set("variant", selection.variant);
  else next.delete("variant");
  return next;
}

export function reviewDraftChanged(draft: ReviewDraft | null): boolean {
  return !!draft && (draft.subject !== draft.original.subject || draft.body !== draft.original.body);
}

/** Pin both the person and revision captured on opening, never the new selection. */
export function reviewEmailChange(draft: ReviewDraft) {
  return {
    expected_revision: draft.revision,
    scope: "email" as const,
    person_code: draft.code,
    position: draft.position,
    ...(draft.position === 1 && draft.variant ? {variant: draft.variant} : {}),
    subject: draft.subject,
    body: draft.body,
  };
}


/** Existing SIREN route; never manufacture a slug or use another entity's metadata. */
export function reviewCompanyLinks(slug: string, company: {
  id: number; tax_id?: string | null; tax_id_type?: string | null; country?: string;
  metadata?: Record<string, unknown>;
}) {
  const siren = company.tax_id?.trim();
  const valid = !!siren && /^\d{9}$/.test(siren) &&
    (company.tax_id_type === "siren" || (!company.tax_id_type && ["FR", "France"].includes(company.country ?? "")));
  let pappers: string | null = null;
  // A stored URL is trusted only for the exact prospect company's SIREN.
  for (const value of [company.metadata?.pappers_url, company.metadata?.url_pappers]) {
    if (!valid || typeof value !== "string") continue;
    try {
      const url = new URL(value);
      if (url.protocol === "https:" && ["www.pappers.fr", "pappers.fr"].includes(url.hostname) &&
        !url.username && !url.password && !url.port &&
        new RegExp(`^/entreprise/(?:[a-z0-9-]+-)?${siren}/?$`, "i").test(url.pathname)) {
        pappers = `${url.origin}${url.pathname}`;
        break;
      }
    } catch { /* Invalid stored links are never opened. */ }
  }
  if (!pappers && valid) pappers = `https://www.pappers.fr/entreprise/${siren}`;
  return { pappers, prism: `/projects/${encodeURIComponent(slug)}/companies/${company.id}` };
}

export type TextDifference = {kind: "same" | "added" | "removed"; text: string};
/** Lossless word diff, bounded for long pasted drafts; whitespace remains exact. */
export function reviewTextDiff(before: string, after: string): TextDifference[] {
  if (before === after) return [{kind: "same", text: before}];
  const a = before.match(/\s+|[^\s]+/g) ?? [];
  const b = after.match(/\s+|[^\s]+/g) ?? [];
  let prefix = 0;
  while (prefix < Math.min(a.length, b.length) && a[prefix] === b[prefix]) prefix++;
  let tail = 0;
  while (tail < Math.min(a.length, b.length) - prefix && a[a.length - tail - 1] === b[b.length - tail - 1]) tail++;
  const left = a.slice(prefix, a.length - tail), right = b.slice(prefix, b.length - tail);
  const out: TextDifference[] = [];
  const add = (kind: TextDifference["kind"], text: string) => {
    if (!text) return;
    const last = out.at(-1);
    if (last?.kind === kind) last.text += text;
    else out.push({kind, text});
  };
  add("same", a.slice(0, prefix).join(""));
  if (left.length * right.length > 1_000_000) {
    add("removed", left.join("")); add("added", right.join(""));
  } else {
    const dp = Array.from({length: left.length + 1}, () => new Uint32Array(right.length + 1));
    for (let i = left.length - 1; i >= 0; i--) for (let j = right.length - 1; j >= 0; j--)
      dp[i][j] = left[i] === right[j] ? 1 + dp[i + 1][j + 1] : Math.max(dp[i + 1][j], dp[i][j + 1]);
    let i = 0, j = 0;
    while (i < left.length || j < right.length) {
      if (i < left.length && j < right.length && left[i] === right[j]) {add("same", left[i++]); j++;}
      else if (i < left.length && (j === right.length || dp[i + 1][j] >= dp[i][j + 1])) add("removed", left[i++]);
      else add("added", right[j++]);
    }
  }
  add("same", tail ? a.slice(-tail).join("") : "");
  return out;
}

export function reviewSteps(record?: PilotRecord) {
  return record?.profile === "CFO_US_GROWTH_V1" ? [...REVIEW_STEPS.slice(0, 2), {position: 3, day: "J+11", label: "Clôture"}] : REVIEW_STEPS;
}
export function companyWebsite(domain?: string | null): string | null {
  if (!domain) return null;
  try {
    const url = new URL(domain.includes("://") ? domain : `https://${domain}`);
    return ["https:", "http:"].includes(url.protocol) && !url.username && !url.password && url.hostname.includes(".") ? url.href : null;
  } catch {return null;}
}
